import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    DateField,
    FunctionField,
    EditButton,
    DeleteButton,
    ShowButton,
    Filter,
    TextInput,
    SearchInput,
    BooleanInput,
    SelectInput,
    DateInput,
    CreateButton,
    useTranslate,
    TopToolbar,
    ExportButton,
    RefreshButton,
    BulkDeleteButton,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Chip,
    Typography,
    Stack,
    Tooltip,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Campaign,
    Image,
    Gradient,
    Visibility,
    TouchApp,
    Schedule,
    Check,
    Close,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const BannerFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.banners.filters.search')}
                source="q"
                alwaysOn
            />
            <SelectInput
                source="display_type"
                label={translate('resources.banners.fields.displayType')}
                choices={[
                    { id: 'image', name: translate('resources.banners.displayTypes.image') },
                    { id: 'gradient', name: translate('resources.banners.displayTypes.gradient') },
                ]}
            />
            <SelectInput
                source="target_type"
                label={translate('resources.banners.fields.targetType')}
                choices={[
                    { id: 'all', name: translate('resources.banners.targetTypes.all') },
                    { id: 'membership', name: translate('resources.banners.targetTypes.membership') },
                ]}
            />
            <BooleanInput
                source="is_active"
                label={translate('resources.banners.fields.isActive')}
            />
            <DateInput
                source="created_after"
                label={translate('resources.banners.filters.createdAfter')}
            />
            <DateInput
                source="created_before"
                label={translate('resources.banners.filters.createdBefore')}
            />
        </Filter>
    );
};

const MobileBannerFilter = (props: any) => {
    return (
        <Filter {...props}>
            <SearchInput source="q" alwaysOn />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1, flexWrap: 'wrap', width: '100%' }}>
        <RefreshButton />
        <ExportButton />
    </Box>
);

const wrappedChipSx = {
    maxWidth: '100%',
    minWidth: 0,
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
        py: 0.5,
    },
};

const StatusChip = ({ status }: { status: string }) => {
    const translate = useTranslate();
    let color: 'default' | 'primary' | 'success' | 'error' = 'default';
    let icon;

    switch (status) {
        case 'scheduled':
            color = 'primary';
            icon = <Schedule fontSize="small" />;
            break;
        case 'active':
            color = 'success';
            icon = <Check fontSize="small" />;
            break;
        case 'expired':
            color = 'error';
            icon = <Close fontSize="small" />;
            break;
        case 'inactive':
            color = 'default';
            break;
    }

    return (
        <Chip
            label={translate(`resources.banners.statuses.${status}`)}
            color={color}
            size="small"
            icon={icon}
            sx={{ height: 'auto', ...wrappedChipSx }}
        />
    );
};

const getRecordStatus = (record: any): string => {
    const now = new Date();
    const validFrom = record.valid_from ? new Date(record.valid_from) : null;
    const validUntil = record.valid_until ? new Date(record.valid_until) : null;

    let status = 'active';

    if (!record.is_active) {
        status = 'inactive';
    } else if (validFrom && now < validFrom) {
        status = 'scheduled';
    } else if (validUntil && now > validUntil) {
        status = 'expired';
    }

    return status;
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/banners/${record.id}/show`)}
                        sx={{
                            py: 1.5,
                            px: 2,
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            overflowX: 'hidden',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 0.5, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                                {record.display_type === 'image' ?
                                    <Image fontSize="small" color="primary" /> :
                                    <Gradient fontSize="small" color="secondary" />
                                }
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                    {record.title}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
                                <StatusChip status={getRecordStatus(record)} />
                                {record.target_type === 'membership' ? (
                                    <Chip
                                        label={`${record.target_membership_levels?.length || 0} levels`}
                                        size="small"
                                        color="primary"
                                        sx={{ height: 20, fontSize: '0.65rem', ...wrappedChipSx }}
                                    />
                                ) : (
                                    <Chip
                                        label={translate('resources.banners.targetTypes.all')}
                                        size="small"
                                        color="default"
                                        sx={{ height: 20, fontSize: '0.65rem', ...wrappedChipSx }}
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    CTR: {record.total_impressions > 0 ? ((record.total_clicks || 0) / record.total_impressions * 100).toFixed(1) : '0'}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    #{record.position}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {record.created_at ? new Date(record.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : ''}
                        </Typography>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

const BannerList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={isMobile ? <MobileBannerFilter /> : <BannerFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                sort={{ field: 'position', order: 'ASC' }}
                perPage={25}
                empty={isMobile ? false : undefined}
                sx={{
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    '& .RaList-main, & .RaList-content, & .RaList-actions, & .RaList-noResults': {
                        maxWidth: '100%',
                        minWidth: 0,
                        overflowX: 'hidden',
                    },
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                        maxWidth: '100%',
                        overflowX: 'hidden',
                    },
                    ...(isMobile && {
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important', width: '100% !important', maxWidth: '100% !important', minWidth: '0 !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important', width: '100% !important', maxWidth: '100% !important', minWidth: '0 !important', px: '0 !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            display: 'flex !important',
                            flexWrap: 'wrap !important',
                            flex: '1 1 100% !important',
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                            margin: '0 !important',
                        },
                        '& .RaFilterForm-spacer': { display: 'none !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput, & .RaFilterForm-input': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                            marginRight: '0 !important',
                        },
                        '& .filter-field .MuiFormControl-root, & .RaFilterForm-input .MuiFormControl-root': {
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                        },
                        '& .filter-field .MuiInputBase-root, & .RaFilterForm-input .MuiInputBase-root': {
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                        },
                        '& .filter-field .MuiInputBase-input, & .RaFilterForm-input .MuiInputBase-input': {
                            minWidth: '0 !important',
                        },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important', px: '0 !important', width: '100% !important', maxWidth: '100% !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                            '& .column-display_type': { width: '100px' },
                            '& .column-title': { minWidth: '200px', flex: 1 },
                            '& .column-target': { width: '90px' },
                            '& .column-status': { width: '85px' },
                            '& .column-stats': { width: '60px' },
                            '& .column-position': { width: '100px' },
                            '& .column-created_at': { width: '120px' },
                            '& .column-actions': { width: '120px' },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.banners.fields.displayType')}
                            render={(record: any) => (
                                <Tooltip title={translate(`resources.banners.displayTypes.${record.display_type}`)}>
                                    <Box display="flex" alignItems="center" justifyContent="center">
                                        {record.display_type === 'image' ?
                                            <Image fontSize="small" color="primary" /> :
                                            <Gradient fontSize="small" color="secondary" />
                                        }
                                    </Box>
                                </Tooltip>
                            )}
                            source="display_type"
                        />

                        <TextField
                            source="title"
                            label={translate('resources.banners.fields.title')}
                            sx={{
                                '& .MuiTypography-root': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }
                            }}
                        />

                        <FunctionField
                            label={translate('resources.banners.fields.targetType')}
                            render={(record: any) => {
                                if (record.target_type === 'membership') {
                                    const levels = record.target_membership_levels?.length || 0;
                                    return (
                                        <Tooltip title={record.target_membership_levels?.join(', ') || 'N/A'}>
                                            <Chip label={`${levels} levels`} size="small" color="primary" />
                                        </Tooltip>
                                    );
                                }
                                return <Chip label={translate('resources.banners.targetTypes.all')} size="small" color="default" />;
                            }}
                            source="target"
                        />

                        <FunctionField
                            label={translate('resources.banners.fields.status')}
                            render={(record: any) => <StatusChip status={getRecordStatus(record)} />}
                            source="status"
                        />

                        <FunctionField
                            label={translate('resources.banners.fields.ctr')}
                            render={(record: any) => {
                                const ctr = record.total_impressions > 0
                                    ? ((record.total_clicks || 0) / record.total_impressions * 100).toFixed(1)
                                    : '0';
                                return (
                                    <Tooltip title={`${record.total_clicks || 0} clicks / ${record.total_impressions || 0} impressions`}>
                                        <Typography variant="body2">
                                            {ctr}%
                                        </Typography>
                                    </Tooltip>
                                );
                            }}
                            source="stats"
                        />

                        <TextField
                            source="position"
                            label={translate('resources.banners.fields.position')}
                        />

                        <DateField
                            source="created_at"
                            label={translate('resources.banners.fields.createdAt')}
                            locales="en-GB"
                            options={{
                                year: '2-digit',
                                month: 'short',
                                day: 'numeric'
                            }}
                        />

                        <FunctionField
                            label=""
                            render={(record: any) => (
                                <Box display="flex" gap={0}>
                                    <Tooltip title={translate('ra.action.show')}>
                                        <span>
                                            <ShowButton record={record} size="small" label="" />
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.edit')}>
                                        <span>
                                            <EditButton record={record} size="small" label="" />
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.delete')}>
                                        <span>
                                            <DeleteButton record={record} size="small" label="" />
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                            source="actions"
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/banners/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default BannerList;
